import { Loader, MailIcon, PhoneIcon } from "lucide-react";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";

import AppleIcon from "@/assets/images/social-auth/apple.svg?react";
import GoogleIcon from "@/assets/images/social-auth/google.svg?react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { useUpdateOrganizationSettingsMutation } from "@/data/mutations";
import { LoginOption } from "@/data/types";

const SocialLoginOptionMeta = {
  [LoginOption.EMAIL]: {
    icon: <MailIcon className="size-4" />,
    label: "Email",
  },
  [LoginOption.GOOGLE]: {
    icon: <GoogleIcon className="size-4" />,
    label: "Google",
  },
  [LoginOption.APPLE]: {
    icon: <AppleIcon className="size-4" />,
    label: "Apple",
  },
  [LoginOption.SMS]: {
    icon: <PhoneIcon className="size-4" />,
    label: "Apple",
  },
  [LoginOption.BLOCKCHAIN]: {
    icon: null,
    label: "Blockchain",
  },
} as const;

const SocialAuthOptions = [
  LoginOption.EMAIL,
  LoginOption.GOOGLE,
  LoginOption.APPLE,
];

type ConsumerAuthSectionProps = {
  loginOptions: LoginOption[];
};

export const ConsumerAuthSection: FC<ConsumerAuthSectionProps> = ({
  loginOptions,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      login_options: loginOptions,
    },
  });

  const updateOrganizationSettingsMutation =
    useUpdateOrganizationSettingsMutation();

  const selectedOptions = form.watch("login_options");

  const allSocialOptionsSelected = SocialAuthOptions.every((option) =>
    selectedOptions.includes(option),
  );

  const handleSocialMasterToggle = (checked: boolean) => {
    if (checked) {
      form.setValue("login_options", [
        ...selectedOptions,
        ...SocialAuthOptions.filter(
          (option) => !selectedOptions.includes(option),
        ),
      ]);
    } else {
      form.setValue(
        "login_options",
        selectedOptions.filter((option) => !SocialAuthOptions.includes(option)),
      );
    }
  };

  const handleOptionToggle = (option: LoginOption, checked: boolean) => {
    if (checked) {
      form.setValue("login_options", [...selectedOptions, option]);
    } else {
      form.setValue(
        "login_options",
        selectedOptions.filter((o) => o !== option),
      );
    }
  };

  const onSubmit = async () => {
    try {
      await updateOrganizationSettingsMutation.mutateAsync({
        login_options: form.getValues("login_options"),
      });
    } finally {
      setModalOpen(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
          Consumer Authentication
        </h3>

        <Card className="mt-6">
          <CardContent className="p-6">
            <p>
              Select the options end-consumers can use to login or signup in
              order to claim digital products
            </p>

            <div>
              <div className="mt-6 flex items-start gap-3">
                <Checkbox
                  id="social"
                  checked={allSocialOptionsSelected}
                  onCheckedChange={handleSocialMasterToggle}
                />

                <div className="grid gap-4">
                  <Label htmlFor="social">Email or Socials</Label>

                  <div className="flex gap-2.5 flex-wrap">
                    {SocialAuthOptions.map((option) => (
                      <Label
                        key={option}
                        className="flex items-start gap-3 border p-4 rounded-lg min-w-54"
                      >
                        <Checkbox
                          checked={selectedOptions.includes(option)}
                          onCheckedChange={(checked) => {
                            if (checked !== "indeterminate") {
                              handleOptionToggle(option, checked);
                            }
                          }}
                        />
                        <div className="flex items-center gap-2">
                          {SocialLoginOptionMeta[option].icon}
                          <p>{SocialLoginOptionMeta[option].label}</p>
                        </div>
                      </Label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-3">
                <Checkbox
                  id="blockchain"
                  checked={selectedOptions.includes(LoginOption.BLOCKCHAIN)}
                  onCheckedChange={(checked) => {
                    if (checked !== "indeterminate") {
                      handleOptionToggle(LoginOption.BLOCKCHAIN, checked);
                    }
                  }}
                />

                <div className="grid gap-2">
                  <Label htmlFor="blockchain">Blockchain</Label>
                  <p className="text-muted-foreground text-sm">
                    Allows consumers to login with Ethereum and ENS addresses
                  </p>
                </div>
              </div>
            </div>
          </CardContent>

          <div className="border-t px-6 py-4">
            <AlertDialog open={modalOpen} onOpenChange={setModalOpen}>
              <AlertDialogTrigger asChild>
                <Button type="button">Save</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Login Changes</AlertDialogTitle>
                  <AlertDialogDescription>
                    <b>Deselecting</b> login options will remove them from any
                    SKU that has them selected. <b>Adding</b> login options will
                    add them as a possible login method for all SKUs in the
                    Organization. Are you sure you want to confirm changes?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                  <Button
                    type="submit"
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={updateOrganizationSettingsMutation.isPending}
                  >
                    {updateOrganizationSettingsMutation.isPending ? (
                      <Loader className="animate-spin" />
                    ) : null}
                    Confirm
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Card>
      </form>
    </Form>
  );
};
